import { api } from "@/services/api";

export const roleManagementApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Organizations
    getRoleManagementOrg: builder.query<any, void>({
      query: () => "/api/account/v1/orgs",
      transformResponse: (response: any) => response?.data?.[0] || null,
      providesTags: ["Org"],
    }),

    // Users
    getRoleManagementOrgUsers: builder.query<any[], string>({
      query: (orgId) => `/api/account/v1/orgs/${orgId}/users`,
      transformResponse: (response: any) =>
        response?.data?.data || response?.data || [],
      providesTags: ["OrgUsers"],
    }),

    addOrgUser: builder.mutation<
      any,
      {
        orgId: string;
        data: { firstname: string; lastname: string; email: string };
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/api/account/v1/orgs/${orgId}/users`,
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OrgUsers"],
    }),

    deleteOrgUser: builder.mutation<void, { orgId: string; orgUserId: string }>(
      {
        query: ({ orgId, orgUserId }) => ({
          url: `/api/account/v1/orgs/${orgId}/users/${orgUserId}`,
          method: "DELETE",
          responseHandler: (response: Response) => response.text(),
        }),
        invalidatesTags: ["OrgUsers"],
      },
    ),

    // User Roles — named distinctly to avoid overriding roleApi.getUserRoles used at login
    getOrgMemberRoles: builder.query<string[], string>({
      query: (orgUserId) => `/api/role/v1/${orgUserId}`,
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        return response?.data || [];
      },
      providesTags: (_result, _error, orgUserId) => [
        { type: "UserRoles", id: orgUserId },
      ],
    }),

    updateOrgMemberRoles: builder.mutation<
      void,
      { orgUserId: string; roles: string[] }
    >({
      query: ({ orgUserId, roles }) => ({
        url: `/api/role/v1/${orgUserId}`,
        method: "PUT",
        body: roles,
        headers: { "Content-Type": "application/json" },
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: (_result, _error, { orgUserId }) => [
        "OrgUsers",
        { type: "UserRoles", id: orgUserId },
      ],
    }),

    // User Portfolios — named distinctly to avoid overriding authApi.getUserPortfolios
    getOrgMemberPortfolios: builder.query<string[], string>({
      query: (orgUserId) => `/api/authz/v1/${orgUserId}/portfolios`,
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        return response?.data || [];
      },
      providesTags: (_result, _error, orgUserId) => [
        { type: "UserPortfolios", id: orgUserId },
      ],
    }),

    updateOrgMemberPortfolios: builder.mutation<
      void,
      { orgUserId: string; portfolios: string[] }
    >({
      query: ({ orgUserId, portfolios }) => ({
        url: `/api/authz/v1/${orgUserId}/portfolios`,
        method: "PUT",
        body: { portfolios },
        headers: { "Content-Type": "application/json" },
        responseHandler: (response: Response) => response.text(),
      }),
      invalidatesTags: (_result, _error, { orgUserId }) => [
        "OrgUsers",
        { type: "UserPortfolios", id: orgUserId },
      ],
    }),

    // User Login (email lookup — view only, no cache tags needed)
    getRoleManagementUserLogin: builder.query<{ email: string }, string>({
      query: (orgUserId) => `/api/account/v1/user_login/${orgUserId}`,
      transformResponse: (response: any) => ({
        email: response?.email ?? response?.data?.email ?? "",
      }),
    }),
  }),
});

export const {
  useGetRoleManagementOrgQuery,
  useGetRoleManagementOrgUsersQuery,
  useAddOrgUserMutation,
  useDeleteOrgUserMutation,
  useLazyGetOrgMemberRolesQuery,
  useUpdateOrgMemberRolesMutation,
  useLazyGetOrgMemberPortfoliosQuery,
  useUpdateOrgMemberPortfoliosMutation,
  useLazyGetRoleManagementUserLoginQuery,
} = roleManagementApi;
